import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, Calendar, Eye, Pin } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Rankings() {
  const { t } = useTranslation();
  const TABS = [
    { key: "rising7", labelKey: "rankings.tab_rising_7", icon: TrendingUp, type: "rising", days: 7 },
    { key: "rising30", labelKey: "rankings.tab_rising_30", icon: Calendar, type: "rising", days: 30 },
    { key: "searched", labelKey: "rankings.tab_searched", icon: Eye, type: "searched" },
  ];
  const [tab, setTab] = useState("rising7");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerms, setSearchTerms] = useState([]);

  useEffect(() => {
    const tcfg = TABS.find((x) => x.key === tab);
    setLoading(true);
    const fetcher =
      tcfg.type === "rising"
        ? api.get("/rankings/rising", { params: { days: tcfg.days, limit: 20 } }).then((r) => r.data.results)
        : api.get("/rankings/searched", { params: { limit: 20 } }).then((r) => r.data.results);
    fetcher.then((data) => setRows(data || [])).finally(() => setLoading(false));
    if (tcfg.type === "searched") {
      api.get("/rankings/search-terms", { params: { limit: 10 } }).then((r) => setSearchTerms(r.data || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const currentTab = TABS.find((x) => x.key === tab);

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8">
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">{t("rankings.label")}</div>
        <h1 className="font-heading font-black text-5xl tracking-tighter">{t("rankings.title")}</h1>
        <p className="text-white/50 mt-2 max-w-2xl">{t("rankings.subtitle")}</p>
      </div>

      <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto">
        {TABS.map(({ key, labelKey, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`rankings-tab-${key}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-heading font-semibold uppercase tracking-wide border-b-2 transition whitespace-nowrap ${
              tab === key ? "border-[#FACC15] text-[#FACC15]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Icon className="w-4 h-4" /> {t(labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-white/40 font-mono py-20 tracking-widest text-xs">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="flat-card p-12 text-center">
          <div className="font-heading font-bold text-xl mb-2">{t("rankings.empty_title")}</div>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            {currentTab.type === "rising" ? t("rankings.empty_rising", { days: currentTab.days }) : t("rankings.empty_searched")}
          </p>
          <Link to="/sets" data-testid="rankings-empty-cta" className="btn-primary mt-6 inline-flex text-xs uppercase tracking-wider">{t("rankings.browse_sets")}</Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {rows.map((r, i) => (
            <Link
              key={r.card_id}
              to={`/cards/${r.card_id}`}
              data-testid={`ranking-row-${r.card_id}`}
              className="flat-card flex items-center gap-4 p-4 group"
            >
              <span className="font-mono text-2xl font-bold text-white/30 w-10 text-center">{String(i + 1).padStart(2, "0")}</span>
              <div className="w-12 h-16 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                {r.card?.image && <img src={r.card.image} alt={r.card.name} className="w-full h-full object-cover" loading="lazy" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-lg tracking-tight truncate group-hover:text-[#FACC15]">{r.card?.name || r.card_id}</div>
                  {r.pinned && (
                    <span className="lang-chip text-[#FACC15] border-[#FACC15]/40 flex-shrink-0">
                      <Pin className="w-3 h-3 inline" /> {t("rankings.pinned")}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-white/40 tracking-wider mt-0.5 truncate">
                  {r.card?.set_name} {r.card?.number ? `· #${r.card.number}` : ""}
                </div>
              </div>
              {currentTab.type === "rising" ? (
                <>
                  <div className="text-right hidden sm:block">
                    <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{t("rankings.avg_now")}</div>
                    <div className="font-mono font-medium">{r.recent_avg} {r.currency}</div>
                  </div>
                  <div className={`text-right font-mono font-bold text-xl ${r.change_pct >= 0 ? "text-[#00E5FF]" : "text-[#FF007F]"}`}>
                    {r.change_pct >= 0 ? "+" : ""}{r.change_pct}%
                  </div>
                </>
              ) : (
                <div className="text-right">
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{t("rankings.views")}</div>
                  <div className="font-mono font-medium text-lg">{r.views}</div>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {currentTab.type === "searched" && searchTerms.length > 0 && (
        <div className="mt-12">
          <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-3">{t("rankings.top_search_terms")}</div>
          <div className="flex flex-wrap gap-2">
            {searchTerms.map((tt) => (
              <Link
                key={tt.term}
                to={`/search?q=${encodeURIComponent(tt.term)}`}
                data-testid={`search-term-${tt.term}`}
                className="lang-chip hover:border-[#FACC15]/60 hover:text-[#FACC15] transition"
              >
                {tt.term} <span className="text-white/30 ml-1">{tt.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
